/* eslint-disable react/prop-types */
// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonAvatar from "components/ArgonAvatar";
import ArgonBadge from "components/ArgonBadge";

import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";

// Images
import team2 from "assets/images/team-2.jpg";
import team3 from "assets/images/team-3.jpg";
import team4 from "assets/images/team-4.jpg";
import ArgonButton from "components/ArgonButton";
import { Link } from "react-router-dom";

function Author({ image, name, email }) {
  return (
    <ArgonBox display="flex" alignItems="center" px={0.5} py={0.1}>
      <ArgonBox mr={2}>
        <ArgonAvatar src={image} alt={name} size="sm" variant="full" />
      </ArgonBox>
      <ArgonBox display="flex" flexDirection="column">
        <ArgonTypography variant="button" fontWeight="medium">
          {name}
        </ArgonTypography>
        <ArgonTypography variant="caption" color="secondary">
          {email}
        </ArgonTypography>
      </ArgonBox>
    </ArgonBox>
  );
}

function Function({ job, org }) {
  return (
    <ArgonBox display="flex" flexDirection="column">
      <ArgonTypography variant="caption" fontWeight="medium" color="text">
        {job}
      </ArgonTypography>
      <ArgonTypography variant="caption" color="secondary">
        {org}
      </ArgonTypography>
    </ArgonBox>
  );
}

function StudentData({ student, client }) {
  return (
    <ArgonBox display="flex" flexDirection="column">
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student}
      </ArgonTypography>
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        Client-{client}
      </ArgonTypography>
    </ArgonBox>
  );
}

// Attendance component
function AttendanceProgress({ percentage, label = "Attendance" }) {
  return (
    <ArgonBox>
      {/* Label */}
      <ArgonBox display="flex" justifyContent="space-between" mb={1}>
        <ArgonTypography variant="caption" fontWeight="medium" color="text">
          {"12 July 2024"}
        </ArgonTypography>
      </ArgonBox>

      {/* Progress Bar Container */}
      <ArgonBox
        width="180%"
        height="10px"
        bgColor="grey-200"
        borderRadius="lg"
        position="relative"
        overflow="hidden"
      >
        {/* Progress Fill */}
        <ArgonBox
          width={`${percentage}%`}
          height="100%"
          // bgColor={percentage >= 80 ? "success" : percentage >= 60 ? "warning" : "error"}
          bgColor="primary"
          borderRadius="lg"
          position="absolute"
          top={0}
          left={0}
        />
      </ArgonBox>
    </ArgonBox>
  );
}

const CreditMappingTableData = (
  handleDeleteClick,
  apiData = [],
  currentPage = 1,
  rowsPerPage = 10
) => ({
  columns: [
    // { name: "author", align: "left" },
    // { name: "function", align: "left" },
    // { name: "status", align: "center" },
    // { name: "employed", align: "center" },
    // { name: "action", align: "center" },

    { name: "Sno", align: "center" },
    { name: "Class name", align: "left" },
    { name: "student", align: "left" },
    { name: "tutors", align: "left" },
    { name: "fee status", align: "left" },
    { name: "cycle type", align: "left" },
    { name: "Status", align: "left" },
    { name: "Total Amount", align: "center" },
    { name: "Created Date", align: "center" },
    { name: "action", align: "center" },
  ],

  rows:
    apiData.length > 0
      ? apiData.map((invoice, index) => {
          // Get tutor details
          const tutorName = invoice.tutorId?.fullName || "N/A";
          const tutorEmail = invoice.tutorId?.email || "N/A";

          // Get parent details
          const parentName = invoice.parentId?.fullName || "N/A";
          const parentPhone = invoice.parentId?.phone || "N/A";

          // Get class name from the first student (assuming all students in the invoice have the same class)
          const className = invoice.students?.[0]?.class?.name || "N/A";

          // Get student names
          const studentNames = invoice.students?.map((s) => s.name).join(", ") || "N/A";

          // Get class session title
          const classTitle = invoice.classSessionId?.[0]?.title || "N/A";

          // Get class status
          const classStatus = invoice.classSessionId?.[0]?.status || "N/A";

          // Format date
          const createdDate = invoice.createdAt
            ? new Date(invoice.createdAt).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "N/A";

          // Status color mapping
          const getStatusColor = (status) => {
            switch (status?.toUpperCase()) {
              case "PAID":
                return "success";
              case "PENDING":
                return "warning";
              case "FAILED":
                return "error";
              case "VOID":
                return "secondary";
              default:
                return "info";
            }
          };

          // Class status color mapping
          const getClassStatusColor = (status) => {
            switch (status?.toUpperCase()) {
              case "SCHEDULED":
                return "success";
              case "COMPLETED":
                return "success";
              case "CANCELLED":
                return "error";
              default:
                return "secondary";
            }
          };

          const serialNumber = (currentPage - 1) * rowsPerPage + index + 1;

          return {
            Sno: (
              <ArgonTypography variant="caption" color="text" fontWeight="medium">
                {serialNumber}
              </ArgonTypography>
            ),
            "Class name": (
              <ArgonTypography variant="caption" color="text" fontWeight="medium">
                {className}
              </ArgonTypography>
            ),
            Status: (
              <ArgonBadge
                variant="gradient"
                badgeContent={classStatus === "SCHEDULED" ? "ACTIVE" : classStatus}
                color={getClassStatusColor(classStatus)}
                size="xs"
                container
              />
            ),
            student: <StudentData student={studentNames} client={parentName} />,
            tutors: (
              <Author
                image={invoice.tutorId?.profileImage || team2}
                name={tutorName}
                email={tutorEmail}
              />
            ),
            "fee status": (
              <ArgonBadge
                variant="gradient"
                badgeContent={invoice.status || "PENDING"}
                color={getStatusColor(invoice.status)}
                size="xs"
                container
              />
            ),
            "cycle type": (
              <ArgonTypography variant="caption" color="text" fontWeight="medium">
                {invoice.cycleType || "N/A"}
              </ArgonTypography>
            ),
            "Total Amount": (
              <ArgonTypography variant="caption" color="text" fontWeight="medium">
                ₹{invoice.totalAmount || 0}
              </ArgonTypography>
            ),
            "Created Date": (
              <ArgonTypography variant="caption" color="text" fontWeight="medium">
                {createdDate}
              </ArgonTypography>
            ),
            action: (
              <ArgonBox display="flex" alignItems="center" gap={0}>
                <ArgonButton
                  variant="text"
                  size="large"
                  component={Link}
                  to={`/creditmapping/view/${invoice._id}`}
                  sx={{ minWidth: "12px", p: 0 }}
                >
                  <VisibilityIcon color="primary" />
                </ArgonButton>
                <ArgonButton
                  variant="text"
                  size="large"
                  sx={{ minWidth: "32px", p: 0 }}
                  onClick={() => handleDeleteClick(invoice._id)}
                >
                  <DeleteIcon color="error" />
                </ArgonButton>
              </ArgonBox>
            ),
          };
        })
      : [],
});

export default CreditMappingTableData;
